import React, { useEffect, useState } from 'react';
import './ThemeSettings.css';

const STORAGE_KEY = 'siteTheme';

const DEFAULT_THEME = {
  bg: '#000000',
  text: '#ffffff',
  accent: '#ffd700',
  hover: '#00ffff',
};

const PRESETS = [
  { name: 'デフォルト', colors: DEFAULT_THEME },
  { name: 'ネイビー', colors: { bg: '#0b1220', text: '#e6edf3', accent: '#ffd65a', hover: '#7dd3fc' } },
  { name: 'セピア', colors: { bg: '#f4ecd8', text: '#3b2f2f', accent: '#8b6914', hover: '#a0522d' } },
  { name: 'ホワイト', colors: { bg: '#ffffff', text: '#1a1a1a', accent: '#b8860b', hover: '#0077cc' } },
  { name: 'ガーリー', colors: { bg: '#fff0f7', text: '#59243c', accent: '#e6397f', hover: '#a437f0' } },
];

function toRgb(hex) {
  const n = hex.replace('#', '');
  const full = n.length === 3 ? n.split('').map((c) => c + c).join('') : n;
  const num = parseInt(full, 16);
  return [(num >> 16) & 255, (num >> 8) & 255, num & 255];
}

function toHex(rgb) {
  return (
    '#' +
    rgb
      .map((v) => Math.min(255, Math.max(0, Math.round(v))).toString(16).padStart(2, '0'))
      .join('')
  );
}

// アクセント色から少し暗い色を作る(節番号のグラデーション用)
function shade(hex, ratio) {
  return toHex(toRgb(hex).map((v) => v * ratio));
}

// 2色の中間色(補助テキスト用: 文字色を背景色側に寄せる)
function mix(hexA, hexB, ratioA) {
  const a = toRgb(hexA);
  const b = toRgb(hexB);
  return toHex(a.map((v, i) => v * ratioA + b[i] * (1 - ratioA)));
}

function luminance(hex) {
  const [r, g, b] = toRgb(hex);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

function applyTheme(theme) {
  const root = document.documentElement;
  root.style.setProperty('--bg', theme.bg);
  root.style.setProperty('--text', theme.text);
  root.style.setProperty('--text-muted', mix(theme.text, theme.bg, 0.65));
  root.style.setProperty('--accent', theme.accent);
  root.style.setProperty('--accent-mid', shade(theme.accent, 0.85));
  root.style.setProperty('--accent-deep', shade(theme.accent, 0.7));
  // ボタン背景: アクセントが暗い色でも、十分明るくなるまで白を混ぜる
  let accentStrong = theme.accent;
  for (let i = 0; i < 6 && luminance(accentStrong) < 0.65; i++) {
    accentStrong = mix(accentStrong, '#ffffff', 0.75);
  }
  root.style.setProperty('--accent-strong', accentStrong);
  root.style.setProperty('--accent-strong-mid', shade(accentStrong, 0.85));
  root.style.setProperty('--accent-contrast', luminance(accentStrong) > 0.5 ? '#000000' : '#ffffff');
  // パネル背景(背景色を文字色側にわずかに寄せた半透明色)と淡い罫線・下地
  const [pr, pg, pb] = toRgb(mix(theme.bg, theme.text, 0.94));
  root.style.setProperty('--panel-bg', `rgba(${pr}, ${pg}, ${pb}, 0.92)`);
  const [tr, tg, tb] = toRgb(theme.text);
  root.style.setProperty('--border-soft', `rgba(${tr}, ${tg}, ${tb}, 0.18)`);
  root.style.setProperty('--bg-soft', `rgba(${tr}, ${tg}, ${tb}, 0.07)`);
  root.style.setProperty('--hover', theme.hover);
}

function clearTheme() {
  const root = document.documentElement;
  [
    '--bg',
    '--text',
    '--text-muted',
    '--accent',
    '--accent-mid',
    '--accent-deep',
    '--accent-strong',
    '--accent-strong-mid',
    '--accent-contrast',
    '--panel-bg',
    '--border-soft',
    '--bg-soft',
    '--hover',
  ].forEach((v) =>
    root.style.removeProperty(v)
  );
}

function loadTheme() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return { ...DEFAULT_THEME, ...JSON.parse(raw) };
  } catch (e) {
    return null;
  }
}

const FIELDS = [
  ['bg', '背景色'],
  ['text', '文字色'],
  ['accent', 'アクセント色'],
  ['hover', 'ホバー色'],
];

function ThemeSettings() {
  const [open, setOpen] = useState(false);
  const [theme, setTheme] = useState(() => loadTheme() || DEFAULT_THEME);

  useEffect(() => {
    const saved = loadTheme();
    if (saved) applyTheme(saved);
  }, []);

  const update = (partial) => {
    const next = { ...theme, ...partial };
    setTheme(next);
    applyTheme(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  };

  const reset = () => {
    setTheme(DEFAULT_THEME);
    clearTheme();
    localStorage.removeItem(STORAGE_KEY);
  };

  return (
    <div className="theme-settings">
      <button
        type="button"
        className="theme-settings-toggle"
        aria-label="配色設定"
        title="配色設定"
        onClick={() => setOpen(!open)}
      >
        ⚙
      </button>
      {open && (
        <div className="theme-settings-panel">
          <div className="theme-settings-heading">配色設定</div>
          <div className="theme-settings-presets">
            {PRESETS.map((p) => (
              <button
                type="button"
                key={p.name}
                className="theme-preset-button"
                style={{
                  background: p.colors.bg,
                  color: p.colors.accent,
                  borderColor: p.colors.accent,
                }}
                onClick={() => update(p.colors)}
              >
                {p.name}
              </button>
            ))}
          </div>
          {FIELDS.map(([key, label]) => (
            <label key={key} className="theme-settings-row">
              <span>{label}</span>
              <input
                type="color"
                value={theme[key]}
                onChange={(e) => update({ [key]: e.target.value })}
              />
            </label>
          ))}
          <button type="button" className="theme-settings-reset" onClick={reset}>
            リセット
          </button>
        </div>
      )}
    </div>
  );
}

export default ThemeSettings;
